import { Injectable } from '@angular/core';
import * as moment from 'moment';
import { DEFAULT_LANGUAGE, SPECIAL_CASE_MMDDYYY_FORMAT, SESSION_VAR_KEY_LANGUAGE, SPECIAL_CASE_COUNTRIES_MMDDYYY_FORMAT, SESSION_VAR_KEY_MARKET } from '@ibe.config';
import * as _ from 'lodash';

@Injectable({
  providedIn: 'root'
})
export class INDRADateService {

	INDRA_formatDate(time: string, format: string): string {
		let language = sessionStorage.getItem('language');
		if (!language || language === '') {
			language = DEFAULT_LANGUAGE;
		}
		const date = moment(time).parseZone();
		return date.locale(language).format(format);
	}

	INDRA_parseZone(time: string): moment.Moment {
		return moment(time).parseZone();
	}

	INDRA_computeDifference(from: string, to: string): moment.Duration {
		const fromDate = moment(from).parseZone();
		const toDate = moment(to).parseZone();
		const duration = moment.duration(toDate.diff(fromDate));
		return duration;
	}

	INDRA_computeDifferenceDateOnly(from: string, to: string): moment.Duration {
		const fromDate = moment(from).parseZone().startOf('day');
		const toDate = moment(to).parseZone().startOf('day');
		const duration = moment.duration(toDate.diff(fromDate));
		return duration;
	}

	INDRA_computeDifferenceInDays(from: string, to: string): number {
		return this.INDRA_computeDifference(from, to).asDays();
	}

	INDRA_computeDifferenceOfDatesWithoutTimeInDays(from: string, to: string): number {
		return this.INDRA_computeDifferenceDateOnly(from, to).asDays();
	}

	INDRA_computeDifferenceInMinutes(from: string, to: string): number {
		return this.INDRA_computeDifference(from, to).asMinutes();
	}

	INDRA_getRemainingTimeInSeconds(date: string) {
		const currentDate = moment().parseZone();
		return this.INDRA_computeDifference(currentDate.toISOString(), date).asSeconds();
	}

	INDRA_getRemainingTimeInHours(date: string) {
		const currentDate = moment().parseZone();
		return this.INDRA_computeDifference(currentDate.toISOString(), date).asHours();
	}

	INDRA_getMonth(date: string): number {
		return moment(date).parseZone().month();
	}

	INDRA_getDate(date: string): number {
		return moment(date).parseZone().date();
	}

	INDRA_add(date: string, amount, unit ) {
		return moment(date).parseZone().add(amount, unit);
	}
	
	INDRA_subtract(date: string, amount, unit ) {
		return moment(date).parseZone().subtract(amount, unit);
	}

	INDRA_isSameDay(from: string, to: string) {
		let flag = false;
		const diff = Math.floor(this.INDRA_computeDifferenceOfDatesWithoutTimeInDays(from, to));
		if (diff === 0) {
			flag = true;
		}
		return flag;
	}

	INDRA_convertDate(date: { year: number, month: number, day: number}): Date {
		let stringDate = '';
		if (date) {
		  stringDate += date.year + '-';
		  stringDate += this.INDRA_padNumber(date.month) + '-';
		  stringDate +=  this.INDRA_padNumber(date.day) + 'T00:00:00.000Z';
		}
		return (new Date(stringDate));
	}

	INDRA_padNumber(value: number) {
		return `0${value}`.slice(-2);
	}

	convertStringDate(date: { year: number, month: number, day: number }): string {
		let stringDate = '';
		if (date) {
		  stringDate += date.year + '-';
		  stringDate += this.INDRA_padNumber(date.month) + '-';
		  stringDate +=  this.INDRA_padNumber(date.day);
		}
		return stringDate;
	}

	getDateString(date: { year: number, month: number, day: number }): string {
		return moment().year(date.year).month(date.month).day(date.day).format('YYYY-MM-DD');
	}

	getYear(date: string): number {
		return new Date(date).getFullYear();
	}

	getMonth(date: string): number {
		return new Date(date).getMonth() + 1;
	}

	getArrayOfYearsFromNow(endYear: number): number[] {
		const year = new Date().getFullYear();
		return _.range(year, endYear);
	}

	getArrayOfYearsFromNowPlus50Years(): number[] {
		const year = new Date().getFullYear();
		return _.range(year, year + 50);
	}

	getArrayOfYearsFromNowMinus100Years(includeThisYear: boolean = true): number[] {
		const year = new Date().getFullYear() + 1;
		const numberOfYears = 100;
		if (includeThisYear) {
			return _.range(year - numberOfYears, year);
		}
		return _.range(year - numberOfYears, year - 1);
	}

	getArrayOfYears(startYear: number, endYear: number): number[] {
		return _.range(startYear, endYear);
	}

	getMonths(maxMonth = 13): number[] {
		return _.range(1, maxMonth);
	}


	getDaysByMonth(month: number, year: number): number[] {
		const dateString = year + '-' + _.padStart('' + month, 2, '0');
		const lastDay = moment(dateString).daysInMonth();
		return _.range(1, lastDay + 1);
	}

	isValidDate(stringDate: string) {
		const [year, month, day] = stringDate.split('-');
		const d = new Date(stringDate);
		if ((d.getFullYear() == +year) && ((d.getMonth() + 1) == +month) && (d.getDate() == +day)) {
			return true;
		}
		return false;
	}

	public toDate(str: string): Date {
		return new Date(str);
	}

	INDRA_convertDateToTicks(date: number) {
		// the number of .net ticks at the unix epoch
		const epochTicks = 621355968000000000;
		// there are 10000 .net ticks per millisecond
		const ticksPerMillisecond = 10000;

		// calculate the total number of .net ticks for your date
		return (epochTicks + (date * ticksPerMillisecond));
	}

	INDRA_getCurrentMonth(): number {
		return this.INDRA_getMonth(moment().toString()) + 1;
	}

	INDRA_formatParsedDate(time: Date, format: string): string {
		let language = sessionStorage.getItem('language');
		if (!language || language === '') {
			language = DEFAULT_LANGUAGE;
		}
		const date = moment(time).parseZone();
		return date.locale(language).format(format);
	}

	// get formatted date depending on stored language
	INDRA_getFormattedDate(date: string | Date | moment.Moment, origFormat?: string): string {
		const newFormat = this.INDRA_getDateFormat();
		return origFormat ? moment(date, origFormat).format(newFormat) : moment(date).format(newFormat);
	}

	// get date format depending on stored language
	INDRA_getDateFormat() {
		return SPECIAL_CASE_MMDDYYY_FORMAT.includes(sessionStorage.getItem(SESSION_VAR_KEY_LANGUAGE)) && SPECIAL_CASE_COUNTRIES_MMDDYYY_FORMAT.includes(sessionStorage.getItem(SESSION_VAR_KEY_MARKET).toLowerCase()) ? 'MM/DD/YYYY' : 'DD/MM/YYYY';
	}

	INDRA_addLeadingZero(val) {
		let option;
		val < 10 ? option = '0' + val : option = val;
		return option;
	}


	INDRA_toISOString(date: Date) {
		var tzo = -date.getTimezoneOffset(),
		dif = tzo >= 0 ? '+' : '-',
		pad = function(num) {
			return (num < 10 ? '0' : '') + num;
		};

		return date.getFullYear() +
			'-' + pad(date.getMonth() + 1) +
			'-' + pad(date.getDate()) +
			'T' + pad(date.getHours()) +
			':' + pad(date.getMinutes()) +
			':' + pad(date.getSeconds()) +
			dif + pad(Math.floor(Math.abs(tzo) / 60)) +
			':' + pad(Math.abs(tzo) % 60);
	}
}
